import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { Button, Link, useMediaQuery, useTheme } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import api from '../../api';
import { resolveTutorialPlaybackUrl } from '../../helpers/tutorials';
import YoutubeTutorialDialog from './YoutubeTutorialDialog';

/**
 * Busca vídeos vinculados a uma page_key e renderiza botão(ões)/link de tutorial.
 * Em mobile, usa mobile_url quando existir; caso contrário, desktop_url.
 */
export default function PageTutorialButtons({
    pageKey,
    buttonLabel = 'Como cadastrar',
    fullWidth = false,
    size = 'medium',
    variant = 'button',
    sx,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [videos, setVideos] = useState([]);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        if (!pageKey) {
            setVideos([]);
            return undefined;
        }
        let cancelled = false;
        api.get('tutorial-videos', { params: { page_key: pageKey } })
            .then((response) => {
                if (cancelled) {
                    return;
                }
                const items = response?.data?.data?.items || response?.data?.items || [];
                setVideos(Array.isArray(items) ? items : []);
            })
            .catch(() => {
                if (!cancelled) {
                    setVideos([]);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [pageKey]);

    const playback = useMemo(() => {
        if (!selected) {
            return { url: '', title: '' };
        }
        return {
            url: resolveTutorialPlaybackUrl(selected, isMobile),
            title: selected.name || buttonLabel,
        };
    }, [selected, isMobile, buttonLabel]);

    if (!videos.length) {
        return null;
    }

    return (
        <>
            {videos.map((video) => {
                const label = videos.length === 1 ? buttonLabel : (video.name || buttonLabel);
                if (variant === 'link') {
                    return (
                        <Link
                            key={video.id || video.uuid}
                            component="button"
                            type="button"
                            variant="body2"
                            underline="hover"
                            onClick={() => setSelected(video)}
                            sx={{
                                cursor: 'pointer',
                                border: 0,
                                background: 'none',
                                p: 0,
                                font: 'inherit',
                                color: 'text.secondary',
                                '&:hover': { color: 'primary.main' },
                                ...sx,
                            }}
                        >
                            {label}
                        </Link>
                    );
                }
                return (
                    <Button
                        key={video.id || video.uuid}
                        variant="outlined"
                        size={size}
                        startIcon={<PlayCircleOutlineIcon />}
                        onClick={() => setSelected(video)}
                        fullWidth={fullWidth}
                        sx={{ textTransform: 'none', fontWeight: 600, ...sx }}
                    >
                        {label}
                    </Button>
                );
            })}
            <YoutubeTutorialDialog
                open={Boolean(selected)}
                onClose={() => setSelected(null)}
                videoUrl={playback.url}
                title={playback.title}
            />
        </>
    );
}
